import { useCallback, useEffect, useRef, useState } from 'react';
import { refreshBilling } from '../../services/billing/billingApi';
import { checkoutPollDelay, CHECKOUT_POLL_MAX_MS, CHECKOUT_SLOW_MS } from './useCheckoutReturn';

const WAITING = ['activating', 'slow'];

/**
 * KAN-866 — the suspended screen's "Confirming your subscription…" wait. After a Resume, a Stripe checkout or a portal
 * trip, the server lifts the lock a moment later (realtime ping, ~2s). Until then this keeps the same wait the Plan &
 * billing page shows: phase 'activating' -> 'slow' at 20s, asking the server to re-read the subscription with a back-off (3s, 5s, 10s) up to
 * 60s; the access level leaving 'suspended' (`lifted`) ends it as 'activated'. Client UX only; the server is the boundary.
 *
 * `onPoll` (optional) runs after each server re-read so the host can reload its billing status.
 *
 * @returns {{ phase: null|'activating'|'slow'|'activated', kind: null|string, start: (kind: string) => void, checkAgain: () => void, dismiss: () => void }}
 */
export default function useReactivationWait({ lifted, onPoll }) {
  const [phase, setPhase] = useState(null);
  const [kind, setKind] = useState(null);
  const alive = useRef(true);
  const running = useRef(false);
  const timer = useRef(null);
  const slowTimer = useRef(null);
  const deadline = useRef(0);
  const waitStart = useRef(0);
  const onPollRef = useRef(onPoll);
  onPollRef.current = onPoll;

  const stopTimers = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (slowTimer.current) { clearTimeout(slowTimer.current); slowTimer.current = null; }
  }, []);

  const tick = useCallback(async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (!alive.current || !running.current) return;
    try {
      // KAN-866: every tick re-reads the access level directly (onPoll), so the lift never depends on the
      // realtime ping alone. KAN-1044 saves calls by spacing the ticks out (3s → 5s → 10s), not by skipping reads.
      await refreshBilling();
      await onPollRef.current?.();
    } catch (_e) { /* keep polling until the deadline */ }
    if (!alive.current || !running.current) return;
    if (Date.now() >= deadline.current) { running.current = false; setPhase((p) => (WAITING.includes(p) ? 'slow' : p)); return; }
    timer.current = setTimeout(tick, checkoutPollDelay(Date.now() - waitStart.current));
  }, []);

  const run = useCallback(() => {
    stopTimers();
    running.current = true;
    waitStart.current = Date.now();
    deadline.current = Date.now() + CHECKOUT_POLL_MAX_MS;
    setPhase('activating');
    slowTimer.current = setTimeout(() => {
      slowTimer.current = null;
      if (alive.current) setPhase((p) => (p === 'activating' ? 'slow' : p));
    }, CHECKOUT_SLOW_MS);
    void tick();
  }, [stopTimers, tick]);

  const start = useCallback((k) => { setKind(k); run(); }, [run]);
  const checkAgain = useCallback(() => { run(); }, [run]);
  const dismiss = useCallback(() => { running.current = false; stopTimers(); setPhase(null); }, [stopTimers]);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; running.current = false; stopTimers(); };
  }, [stopTimers]);

  // The lock lifted while we were waiting: the "You're all set" beat.
  useEffect(() => {
    if (lifted && WAITING.includes(phase)) {
      running.current = false;
      stopTimers();
      setPhase('activated');
    }
  }, [lifted, phase, stopTimers]);

  return { phase, kind, start, checkAgain, dismiss };
}
